import { APP_CONFIG } from 'src/config.app'
import { ADAxios, ApprovalServiceApiActions } from 'src/services/AxiosInterceptor'

export type ModuleAIReviewStatus = 'NOT_REQUESTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'

export type ModuleAIReviewSeverity = 'HIGH' | 'MEDIUM' | 'LOW'

export interface ModuleAIReviewFinding {
    findingId: string
    ruleId: string
    severity: ModuleAIReviewSeverity
    message: string
    entityId?: string
    attributePath?: string
    locale?: string
}

export interface ModuleAIReviewSeverityCounts {
    high?: number
    medium?: number
    low?: number
}

export interface ModuleAIReviewSection {
    sectionId: string
    title?: string
    status: 'FINDINGS' | 'PASS' | 'NOT_APPLICABLE'
    rulesEvaluated?: number
    counts?: ModuleAIReviewSeverityCounts
    findings?: ModuleAIReviewFinding[]
}

/** COMPLETED, SKIPPED_TOO_LARGE (only the automated checks ran) or NOT_APPLICABLE. */
export type ModuleAIReviewModelStatus = 'COMPLETED' | 'SKIPPED_TOO_LARGE' | 'NOT_APPLICABLE'

export interface ModuleAIReviewSummary extends ModuleAIReviewSeverityCounts {
    total?: number
    sectionsWithFindings?: number
    model?: {
        status?: ModuleAIReviewModelStatus
        modelId?: string
        promptVersion?: string
    }
}

export interface ModuleAIReview {
    status: ModuleAIReviewStatus
    entityType?: string
    versionId?: string
    summary?: ModuleAIReviewSummary
    sections?: ModuleAIReviewSection[]
    /** Epoch milliseconds. */
    startedAt?: number
    /** Epoch milliseconds. */
    completedAt?: number
    failureReason?: string
}

/**
 * Client for the advisory AI review of a module version's latest saved draft (GetDraftAIReview on
 * ApprovalService). The review runs asynchronously after every draft save, so callers poll while it
 * is IN_PROGRESS. A version that was never reviewed comes back as NOT_REQUESTED rather than an error.
 */
export class ModuleAIReviewService {
    public static async getModuleAIReview(
        moduleVersionId: string,
        abortController?: AbortController
    ): Promise<ModuleAIReview> {
        const { data }: { data: { draftAIReview: ModuleAIReview } } = await ADAxios.get(
            `${APP_CONFIG.approvalUrl}/draft-ai-reviews/MODULE/${encodeURIComponent(
                moduleVersionId
            )}`,
            {
                apiActionName: ApprovalServiceApiActions.GetDraftAIReview,
                signal: abortController?.signal,
            }
        )
        return data.draftAIReview
    }
}
